import { FlatList } from "react-native";
import { useAtomValue } from "jotai";

import { ScreenLayout } from "@/blocks/ScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { useListRedeterminationsQuery } from "@/data/queries/redeterminationsQueries";
import { filterRenewalsForCatalog } from "@/domain/dashboardCards";
import { useIsDevToolsEnabled } from "@/hooks/useIsDevToolsEnabled";
import { dashboardScenarioAtom } from "@/stores/atoms";
import { formatLongDate } from "@/utils/date";

export const MedicaidRenewalsScreen = () => {
  const isDevToolsEnabled = useIsDevToolsEnabled();

  const { data: fetchedRedeterminations, isLoading } =
    useListRedeterminationsQuery({
      filters: {
        status: ["NOT_STARTED", "PENDING_FORM", "CALL_BACK", "COMPLETED"],
      },
    });

  // Mirror the dashboard's DEV scenario override so the panel drives this
  // screen too (non-production internal staff only).
  const scenario = useAtomValue(dashboardScenarioAtom);
  const isScenarioActive = isDevToolsEnabled && scenario !== null;
  const source = isScenarioActive
    ? (scenario?.redeterminations ?? [])
    : (fetchedRedeterminations ?? []);

  const renewals = filterRenewalsForCatalog(source);

  if (isLoading && !isScenarioActive) {
    return (
      <ScreenLayout>
        <VnBox className="flex-1 items-center justify-center">
          <VnLoadingSpinner />
        </VnBox>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout variant="LIST">
      <VnBox className="flex-1 gap-5 pt-4">
        <VnText
          variant="headerTwo"
          i18nKey="native:redeterminations.medicaidRenewals"
        />

        <FlatList
          data={renewals}
          keyExtractor={item => item.id}
          contentContainerClassName="gap-4 pb-8"
          renderItem={({ item }) => (
            <VnCard className="gap-2">
              <VnText
                variant="lgMedium"
                i18nKey="native:redeterminations.status"
                i18nOptions={{ context: item.status }}
              />
              <VnText
                variant="md"
                className="text-muted-foreground"
                i18nKey="native:redeterminations.due"
                i18nOptions={{
                  date: formatLongDate(item.redeterminationDate),
                }}
              />
            </VnCard>
          )}
          ListEmptyComponent={
            <VnText
              variant="md"
              className="text-muted-foreground text-center"
              i18nKey="native:redeterminations.noRenewals"
            />
          }
        />
      </VnBox>
    </ScreenLayout>
  );
};
